import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import {
    ShieldCheck,
    AlertTriangle,
    CheckCircle2,
    Loader2,
    LayoutDashboard,
    RotateCcw,
    FileText,
    Search,
    BrainCircuit,
    ChevronRight,
    Scale,
    Activity,
    ClipboardCheck,
    Info
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useApiClient } from "@/hooks/use-api-client";
import { useAgentExecution } from "@/contexts/AgentExecutionContext";
import DashboardBreadcrumbs from "@/components/ui/DashboardBreadcrumbs";
import { Badge } from "../../components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";
import { UsageTracker } from "@/lib/services/usage-tracker";

// ─── Types ───────────────────────────────────────────────────────────────────

interface ComplianceResult {
    status?: "processing" | "completed" | "failed" | "rejected";
    message?: string;
    guard_rail_details?: {
        is_valid: boolean;
        confidence: number;
        reason: string;
    };
    compliance_score?: number;
    risk_level?: string;
    findings?: Array<{
        requirement: string;
        status: "compliant" | "non-compliant" | "partial";
        finding: string;
        legal_basis: string;
    }>;
    recommendations?: Array<{
        title: string;
        priority: "high" | "medium" | "low";
        action: string;
    }>;
}

interface ComplianceForm {
    organization_type: string;
    jurisdiction: string;
    regulatory_framework: string[];
    process_description: string;
}

// ─── Premium Styles ──────────────────────────────────────────────────────────

function PremiumStyles() {
    return (
        <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

      .cmv2-root {
        font-family: 'Inter', system-ui, -apple-system, sans-serif;
        background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #f3f4f6 100%);
        min-height: 100vh;
      }

      .cmv2-header {
        background: linear-gradient(135deg, #14532d 0%, #166534 40%, #15803d 100%);
        padding: 2rem 2.5rem;
        position: relative;
        overflow: hidden;
      }
      .cmv2-header::before {
        content: '';
        position: absolute;
        inset: 0;
        background: radial-gradient(circle at 20% 80%, rgba(187,247,208,0.15) 0%, transparent 60%);
      }
      .cmv2-header-content { position: relative; z-index: 1; }

      .cmv2-body { display: flex; min-height: calc(100vh - 140px); }
      .cmv2-form-panel {
        width: 35%;
        min-width: 360px;
        background: #fff;
        border-right: 1px solid #bbf7d0;
        padding: 2rem;
        overflow-y: auto;
      }
      .cmv2-analytics-panel { flex: 1; padding: 2rem; overflow-y: auto; }

      @media (max-width: 768px) {
        .cmv2-header {
          padding: 1.25rem 1rem !important;
        }
        .cmv2-body {
          flex-direction: column !important;
          min-height: auto !important;
        }
        .cmv2-form-panel {
          width: 100% !important;
          min-width: 100% !important;
          border-right: none !important;
          border-bottom: 1px solid #bbf7d0 !important;
          padding: 1.25rem !important;
        }
        .cmv2-analytics-panel {
          width: 100% !important;
          padding: 1.25rem !important;
        }
      }

      .cmv2-field-group { margin-bottom: 1.5rem; animation: slideUp 0.4s ease-out; }
      .cmv2-label {
        font-size: 0.7rem;
        font-weight: 900;
        color: #166534;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        margin-bottom: 0.5rem;
        display: flex;
        align-items: center;
        gap: 0.4rem;
      }

      .cmv2-card {
        background: #fff;
        border: 1px solid #f0fdf4;
        border-radius: 1.5rem;
        padding: 1.5rem;
        box-shadow: 0 4px 20px -5px rgba(22,101,52,0.05);
      }

      @keyframes slideUp { 
        from { opacity: 0; transform: translateY(8px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `}</style>
    );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function ComplianceMonitoringV2() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const apiClient = useApiClient();
    const { startComplianceRun, getRun } = useAgentExecution();
    const isMobile = useIsMobile();
    const [mobileTab, setMobileTab] = useState<"form" | "results">("form");

    const [form, setForm] = useState<ComplianceForm>({
        organization_type: "Entidad Financiera",
        jurisdiction: "Colombia",
        regulatory_framework: ["Ley 1581 de 2012", "Circular 029 de 2014"],
        process_description: ""
    });

    const runId = "compliance-run";
    const currentRun = getRun(runId);
    const loading = currentRun?.status === "loading";
    const result = currentRun?.metadata as ComplianceResult;

    const handleRunAnalysis = async () => {
        if (!form.process_description.trim()) {
            toast.error("Por favor, describa el proceso o situación a analizar.");
            return;
        }

        // Track usage for dashboard
        if (user) {
            UsageTracker.trackComplianceMonitor(user.id, "Auditoría iniciada", form.organization_type, undefined);
        }

        if (isMobile) {
            setMobileTab("results");
        }

        await startComplianceRun(runId, form, apiClient, user?.id || null);
    };

    return (
        <div className="cmv2-root">
            <PremiumStyles />

            <div className="cmv2-header">
                <div className="cmv2-header-content">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-white/10 backdrop-blur-xl flex items-center justify-center border border-white/20">
                                <ShieldCheck className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-black text-white tracking-tighter uppercase italic">Cumplimiento Normativo V2</h1>
                                <p className="text-emerald-200 text-[10px] font-bold tracking-widest uppercase opacity-70">Regulatory & Ethics Engine</p>
                            </div>
                        </div>
                        <Button variant="ghost" className="text-white hover:bg-white/10" onClick={() => navigate("/dashboard")}>
                            <LayoutDashboard className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>

            {/* Mobile Tab Switcher */}
            {isMobile && (result || loading) && (
                <div className="bg-white border-b border-emerald-100 p-2 sticky top-0 z-30 shadow-sm">
                    <div className="flex items-center gap-1 bg-emerald-50 p-1 rounded-xl">
                        <button
                            type="button"
                            onClick={() => setMobileTab("form")}
                            className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all ${
                                mobileTab === "form"
                                    ? "bg-white text-emerald-800 shadow-sm"
                                    : "text-slate-600 hover:text-slate-900"
                            }`}
                        >
                            📝 Parámetros
                        </button>
                        <button
                            type="button"
                            onClick={() => setMobileTab("results")}
                            className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                                mobileTab === "results"
                                    ? "bg-white text-emerald-800 shadow-sm"
                                    : "text-slate-600 hover:text-slate-900"
                            }`}
                        >
                            <ShieldCheck className="h-3.5 w-3.5" />
                            <span>Auditoría</span>
                            {result?.compliance_score !== undefined ? (
                                <span className="bg-emerald-600 text-white text-[10px] px-1.5 py-0.2 rounded-full font-black">
                                    {result.compliance_score}%
                                </span>
                            ) : loading ? (
                                <Loader2 className="h-3 w-3 animate-spin text-emerald-600" />
                            ) : null}
                        </button>
                    </div>
                </div>
            )}

            <div className="cmv2-body">
                {(!isMobile || !result || mobileTab === "form") && (
                <div className="cmv2-form-panel">
                    <div className="space-y-6">
                        <div className="cmv2-field-group">
                            <div className="cmv2-label"><Scale className="h-4 w-4" /> Tipo de Organización</div>
                            <Input
                                value={form.organization_type}
                                onChange={(e) => setForm(f => ({ ...f, organization_type: e.target.value }))}
                                className="border-emerald-100 rounded-xl"
                            />
                        </div>

                        <div className="cmv2-field-group">
                            <div className="cmv2-label"><FileText className="h-4 w-4" /> Marco Normativo (Separado por comas)</div>
                            <Input
                                value={form.regulatory_framework.join(", ")}
                                onChange={(e) => setForm(f => ({ ...f, regulatory_framework: e.target.value.split(",").map(s => s.trim()) }))}
                                className="border-emerald-100 rounded-xl"
                            />
                        </div>

                        <div className="cmv2-field-group">
                            <div className="cmv2-label"><Activity className="h-4 w-4" /> Proceso / Actividad a Evaluar</div>
                            <Textarea
                                value={form.process_description}
                                onChange={(e) => setForm(f => ({ ...f, process_description: e.target.value }))}
                                placeholder="Describa el flujo de datos o proceso corporativo..."
                                className="border-emerald-100 min-h-[150px] rounded-xl text-sm"
                            />
                        </div>

                        <Button
                            className="w-full h-12 bg-emerald-900 hover:bg-emerald-800 text-white font-black rounded-xl gap-2 shadow-xl shadow-emerald-100"
                            onClick={handleRunAnalysis}
                            disabled={loading}
                        >
                            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShieldCheck className="h-5 w-5" />}
                            Iniciar Auditoría AI
                        </Button>

                        {currentRun?.traceSteps && (
                            <div className="mt-8 space-y-3 px-2 border-t pt-6">
                                <h4 className="text-[10px] font-black text-emerald-800 uppercase tracking-widest mb-4">Pipeline de Ejecución</h4>
                                {currentRun.traceSteps.map((s, i) => (
                                    <div key={i} className="flex items-center gap-3 animate-in fade-in slide-in-from-left-2">
                                        <span className="text-lg">{s.emoji}</span>
                                        <span className="text-[10px] font-bold text-slate-600 uppercase">{s.message}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                )}

                {(!isMobile || ((result || loading) && mobileTab === "results")) && (
                <div className="cmv2-analytics-panel">
                    {!result && !loading ? (
                        <div className="flex flex-col items-center justify-center h-full text-center">
                            <div className="w-24 h-24 bg-emerald-50 rounded-full flex items-center justify-center mb-6">
                                <ClipboardCheck className="h-12 w-12 text-emerald-200" />
                            </div>
                            <h2 className="text-2xl font-black text-emerald-900 tracking-tight">Análisis de Riesgo Regulatorio</h2>
                            <p className="text-slate-400 mt-2 max-w-sm font-medium text-sm">
                                Evalúe sus procesos internos frente a leyes y regulaciones locales e internacionales con precisión quirúrgica.
                            </p>
                        </div>
                    ) : result?.status === 'rejected' ? (
                        <div className="max-w-2xl mx-auto py-12">
                            <div className="cmv2-card border-t-4 border-t-red-500 shadow-2xl">
                                <div className="flex items-start gap-6 mb-8">
                                    <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center text-red-600">
                                        <AlertTriangle className="h-9 w-9" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-black text-slate-900 mb-2">Consulta Fuera de Alcance</h3>
                                        <p className="text-slate-500 font-medium leading-relaxed">
                                            "{result.guard_rail_details?.reason || result.message}"
                                        </p>
                                    </div>
                                </div>
                                <Button className="w-full bg-slate-900 text-white font-bold h-12 rounded-xl" onClick={() => window.location.reload()}>
                                    Nueva Evaluación
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="max-w-5xl mx-auto space-y-6 animate-in fade-in duration-700">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* Score Widget */}
                                <div className="cmv2-card flex flex-col items-center justify-center p-8 bg-gradient-to-br from-white to-emerald-50/30">
                                    <div className="cmv2-label mb-4">Índice de Cumplimiento</div>
                                    <div className="relative w-32 h-32 flex items-center justify-center">
                                        <svg className="absolute inset-0 w-full h-full -rotate-90">
                                            <circle cx="64" cy="64" r="58" stroke="#f0fdf4" strokeWidth="10" fill="none" />
                                            <circle
                                                cx="64" cy="64" r="58" stroke="#059669" strokeWidth="10" fill="none"
                                                strokeDasharray="364"
                                                strokeDashoffset={364 - (364 * (result?.compliance_score || 0) / 100)}
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                        <span className="text-3xl font-black text-emerald-900">{result?.compliance_score || 0}%</span>
                                    </div>
                                    <Badge className="mt-6 bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-none px-4 py-1 font-black text-[10px] uppercase">
                                        Riesgo: {result?.risk_level || "No calculado"}
                                    </Badge>
                                </div>

                                {/* Findings */}
                                <div className="lg:col-span-2 space-y-4">
                                    <div className="flex items-center gap-2 px-2">
                                        <Search className="h-5 w-5 text-emerald-600" />
                                        <h3 className="font-black text-emerald-900 text-xs uppercase italic">Hallazgos Críticos</h3>
                                    </div>
                                    <div className="space-y-3">
                                        {result?.findings?.map((f, i) => (
                                            <div key={i} className="cmv2-card p-4 flex gap-4 items-start border-l-4 border-l-emerald-500">
                                                <div className={`mt-1 p-1 rounded ${f.status === 'compliant' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                    {f.status === 'compliant' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
                                                </div>
                                                <div>
                                                    <h4 className="font-bold text-slate-900 text-sm mb-1">{f.requirement}</h4>
                                                    <p className="text-xs text-slate-500 mb-2">{f.finding}</p>
                                                    <div className="flex items-center gap-1.5 opacity-70">
                                                        <Info size={12} className="text-emerald-600" />
                                                        <span className="text-[10px] font-black text-emerald-800 uppercase">{f.legal_basis}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )) || <p className="text-xs text-slate-400 p-8 font-medium italic">Esperando resultados de la auditoría...</p>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                )}
            </div>
        </div>
    );
}
